import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import AppShell from "./AppShell";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, LineChart, Line, Legend
} from "recharts";
import {
  TrendingUp, DollarSign, FileText, Clock,
  Calendar, ChevronDown, Users as UsersIcon, Filter, ArrowDown, ArrowUp, Receipt
} from "lucide-react";

const PRESET_RANGES = [
  { label: "Last 12 months", value: "12m" },
  { label: "Last 6 months", value: "6m" },
  { label: "Last 3 months", value: "3m" },
  { label: "This year", value: "year" },
  { label: "Custom", value: "custom" }
];

const Analytics = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [expData, setExpData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rangePreset, setRangePreset] = useState("12m");
  const [showRangeMenu, setShowRangeMenu] = useState(false);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [planInfo, setPlanInfo] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [showClientFilter, setShowClientFilter] = useState(false);
  const rangeRef = useRef(null);
  const clientRef = useRef(null);

  useEffect(() => {
    fetchPlanInfo();
    fetchAnalytics();
    fetchClients();
    fetchExpenseAnalytics();
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (rangeRef.current && !rangeRef.current.contains(e.target)) setShowRangeMenu(false);
      if (clientRef.current && !clientRef.current.contains(e.target)) setShowClientFilter(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchPlanInfo = async () => {
    try {
      const { data } = await axios.get(`${API}/user/plan`);
      setPlanInfo(data);
    } catch (e) {}
  };

  const fetchClients = async () => {
    try {
      const { data } = await axios.get(`${API}/clients`);
      setClients(data);
    } catch (e) {}
  };

  const fetchExpenseAnalytics = async (start = null, end = null, clientId = null) => {
    try {
      let url = `${API}/expenses/analytics/summary`;
      const params = [];
      if (start) params.push(`from_date=${start}`);
      if (end) params.push(`to_date=${end}`);
      if (clientId) params.push(`client_id=${clientId}`);
      if (params.length > 0) url += `?${params.join("&")}`;
      const { data } = await axios.get(url);
      setExpData(data);
    } catch (e) {}
  };

  const fetchAnalytics = async (start = null, end = null, clientId = null) => {
    setLoading(true);
    try {
      let url = `${API}/invoices/analytics`;
      const params = [];
      if (start && end) { params.push(`start=${start}`, `end=${end}`); }
      if (clientId) { params.push(`client_id=${clientId}`); }
      if (params.length > 0) { url += `?${params.join("&")}`; }
      const { data } = await axios.get(url);
      setData(data);
    } catch (err) {
      console.error("Failed to fetch analytics:", err);
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = (preset) => {
    const now = new Date();
    const end = now.toISOString().split("T")[0];
    let start;
    if (preset === "12m") start = new Date(now.getFullYear() - 1, now.getMonth(), 1).toISOString().split("T")[0];
    else if (preset === "6m") start = new Date(now.getFullYear(), now.getMonth() - 6, 1).toISOString().split("T")[0];
    else if (preset === "3m") start = new Date(now.getFullYear(), now.getMonth() - 3, 1).toISOString().split("T")[0];
    else if (preset === "year") start = `${now.getFullYear()}-01-01`;
    else return null;
    return { start, end };
  };

  const handleRangeChange = (preset) => {
    setRangePreset(preset);
    if (preset !== "custom") {
      setShowRangeMenu(false);
      const range = getDateRange(preset);
      if (range) {
        fetchAnalytics(range.start, range.end, selectedClient || null);
        fetchExpenseAnalytics(range.start, range.end, selectedClient || null);
      }
    }
  };

  const handleCustomRange = () => {
    if (customStart && customEnd) {
      setShowRangeMenu(false);
      fetchAnalytics(customStart, customEnd, selectedClient || null);
      fetchExpenseAnalytics(customStart, customEnd, selectedClient || null);
    }
  };

  const handleClientChange = (clientId) => {
    setSelectedClient(clientId);
    setShowClientFilter(false);
    const range = rangePreset !== "custom" ? getDateRange(rangePreset) : (customStart && customEnd ? { start: customStart, end: customEnd } : null);
    fetchAnalytics(range?.start || null, range?.end || null, clientId || null);
    fetchExpenseAnalytics(range?.start || null, range?.end || null, clientId || null);
  };

  const formatMonth = (month) => {
    const [y, m] = month.split("-");
    const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${names[parseInt(m) - 1]} '${y.slice(2)}`;
  };

  const formatCurrency = (val) => `$${(val || 0).toLocaleString()}`;

  const chartData = data?.months?.map(m => ({
    name: formatMonth(m.month),
    month: m.month,
    Paid: m.total_paid,
    Outstanding: m.total_sent,
    invoices: m.total_invoices
  })) || [];

  const statusData = data?.status_counts ? [
    { name: "Draft", value: data.status_counts.draft, color: "#6B7280" },
    { name: "Sent", value: data.status_counts.sent, color: "#F59E0B" },
    { name: "Paid", value: data.status_counts.paid, color: "#10B981" },
    { name: "Overdue", value: data.status_counts.overdue, color: "#EF4444" }
  ].filter(s => s.value > 0) : [];

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-[#0A0A0A] text-white p-3 text-[13px] border-0">
        <p className="font-semibold mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }}>{p.name}: ${p.value.toLocaleString()}</p>
        ))}
      </div>
    );
  };

  const collectionRate = data?.total_invoices > 0
    ? Math.round((data.total_revenue / Math.max(data.total_revenue + data.total_outstanding, 1)) * 100)
    : 0;

  const selectedClientName = selectedClient ? clients.find(c => c.id === selectedClient)?.name : null;

  return (
    <AppShell active="analytics" planInfo={planInfo}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="font-display text-[22px] sm:text-[28px] tracking-[-0.03em] text-[#0A0A0A]">Analytics</h1>
          <p className="text-[13px] sm:text-[14px] text-[#6B7280] mt-1">Track your invoicing performance</p>
        </div>

        <div className="flex items-center gap-2">
          {/* Client Filter */}
          {clients.length > 0 && (
            <div className="relative" ref={clientRef} data-testid="client-filter">
              <button
                onClick={() => setShowClientFilter(!showClientFilter)}
                className={`flex items-center gap-2 px-3 py-2 bg-white border text-[13px] font-medium transition-colors ${selectedClient ? "border-[#1A3DFF] text-[#1A3DFF]" : "border-[#E5E7EB] text-[#0A0A0A] hover:border-[#1A3DFF]"}`}
                data-testid="client-filter-btn"
              >
                <Filter className="w-4 h-4 text-[#6B7280]" />
                {selectedClientName || "All clients"}
                <ChevronDown className="w-3.5 h-3.5 text-[#6B7280]" />
              </button>
              {showClientFilter && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-[#E5E7EB] shadow-lg z-20 w-56 max-h-60 overflow-y-auto" data-testid="client-filter-menu">
                  <button
                    onClick={() => handleClientChange("")}
                    className={`block w-full text-left px-4 py-2.5 text-[13px] hover:bg-[#F9FAFB] transition-colors ${!selectedClient ? "bg-[#F3F4F6] font-semibold text-[#0A0A0A]" : "text-[#6B7280]"}`}
                    data-testid="client-all"
                  >
                    All clients
                  </button>
                  {clients.map(c => (
                    <button
                      key={c.id}
                      onClick={() => handleClientChange(c.id)}
                      className={`block w-full text-left px-4 py-2.5 text-[13px] hover:bg-[#F9FAFB] transition-colors ${selectedClient === c.id ? "bg-[#F3F4F6] font-semibold text-[#0A0A0A]" : "text-[#6B7280]"}`}
                      data-testid={`client-${c.id}`}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Date Range Selector */}
          <div className="relative" ref={rangeRef} data-testid="date-range-selector">
            <button
              onClick={() => setShowRangeMenu(!showRangeMenu)}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-[#E5E7EB] text-[13px] font-medium text-[#0A0A0A] hover:border-[#1A3DFF] transition-colors"
              data-testid="date-range-btn"
            >
              <Calendar className="w-4 h-4 text-[#6B7280]" />
              {PRESET_RANGES.find(r => r.value === rangePreset)?.label || "Last 12 months"}
              <ChevronDown className="w-3.5 h-3.5 text-[#6B7280]" />
            </button>
            {showRangeMenu && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-[#E5E7EB] shadow-lg z-20 w-56" data-testid="date-range-menu">
                {PRESET_RANGES.filter(r => r.value !== "custom").map(r => (
                  <button
                    key={r.value}
                    onClick={() => handleRangeChange(r.value)}
                    className={`block w-full text-left px-4 py-2.5 text-[13px] hover:bg-[#F9FAFB] transition-colors ${rangePreset === r.value ? "bg-[#F3F4F6] font-semibold text-[#0A0A0A]" : "text-[#6B7280]"}`}
                    data-testid={`range-${r.value}`}
                  >
                    {r.label}
                  </button>
                ))}
                <button
                  onClick={() => setRangePreset("custom")}
                  className={`block w-full text-left px-4 py-2.5 text-[13px] hover:bg-[#F9FAFB] transition-colors ${rangePreset === "custom" ? "bg-[#F3F4F6] font-semibold text-[#0A0A0A]" : "text-[#6B7280]"}`}
                  data-testid="range-custom"
                >
                  Custom
                </button>
                {rangePreset === "custom" && (
                  <div className="p-3 border-t border-[#E5E7EB] space-y-2" onClick={(e) => e.stopPropagation()}>
                    <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="input text-[13px] w-full" data-testid="custom-start" />
                    <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="input text-[13px] w-full" data-testid="custom-end" />
                    <button onClick={handleCustomRange} className="btn-primary w-full text-[13px] py-2" data-testid="apply-custom-range">Apply</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center"><p className="text-[14px] text-[#6B7280]">Loading analytics...</p></div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-6 sm:mb-8">
            <div className="bg-white border border-[#E5E7EB] p-3 sm:p-5" data-testid="stat-revenue">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-4 h-4 text-[#059669]" />
                <span className="text-[10px] sm:text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase">Revenue</span>
              </div>
              <p className="font-display text-[20px] sm:text-[26px] tracking-[-0.02em] text-[#0A0A0A]">{formatCurrency(data?.total_revenue)}</p>
            </div>
            <div className="bg-white border border-[#E5E7EB] p-3 sm:p-5" data-testid="stat-outstanding">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-[#D97706]" />
                <span className="text-[10px] sm:text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase">Outstanding</span>
              </div>
              <p className="font-display text-[20px] sm:text-[26px] tracking-[-0.02em] text-[#0A0A0A]">{formatCurrency(data?.total_outstanding)}</p>
            </div>
            <div className="bg-white border border-[#E5E7EB] p-3 sm:p-5" data-testid="stat-invoices">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-[#1A3DFF]" />
                <span className="text-[10px] sm:text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase">Invoices</span>
              </div>
              <p className="font-display text-[20px] sm:text-[26px] tracking-[-0.02em] text-[#0A0A0A]">{data?.total_invoices || 0}</p>
            </div>
            <div className="bg-white border border-[#E5E7EB] p-3 sm:p-5" data-testid="stat-avg">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-[#7C3AED]" />
                <span className="text-[10px] sm:text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase">Avg Invoice</span>
              </div>
              <p className="font-display text-[20px] sm:text-[26px] tracking-[-0.02em] text-[#0A0A0A]">{formatCurrency(data?.avg_invoice_value)}</p>
            </div>
            <div className="bg-white border border-[#E5E7EB] p-3 sm:p-5 col-span-2 lg:col-span-1" data-testid="stat-collection">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-[#059669]" />
                <span className="text-[10px] sm:text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase">Collection Rate</span>
              </div>
              <p className="font-display text-[20px] sm:text-[26px] tracking-[-0.02em] text-[#0A0A0A]">{collectionRate}%</p>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
            <div className="lg:col-span-2 bg-white border border-[#E5E7EB] p-4 sm:p-6" data-testid="revenue-chart">
              <h2 className="font-semibold text-[16px] text-[#0A0A0A] mb-1">Monthly Revenue</h2>
              <p className="text-[13px] text-[#6B7280] mb-5">Paid vs outstanding per month</p>
              <div className="h-[260px] sm:h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={{ stroke: "#E5E7EB" }} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v.toLocaleString()}`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="Paid" fill="#1A3DFF" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="Outstanding" fill="#E5E7EB" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center gap-6 mt-3 pt-3 border-t border-[#E5E7EB]">
                <div className="flex items-center gap-2"><div className="w-3 h-3 bg-[#1A3DFF]" /><span className="text-[12px] text-[#6B7280]">Paid</span></div>
                <div className="flex items-center gap-2"><div className="w-3 h-3 bg-[#E5E7EB]" /><span className="text-[12px] text-[#6B7280]">Outstanding</span></div>
              </div>
            </div>

            <div className="space-y-4 sm:space-y-6">
              <div className="bg-white border border-[#E5E7EB] p-4 sm:p-6" data-testid="status-breakdown">
                <h2 className="font-semibold text-[16px] text-[#0A0A0A] mb-4">Status Breakdown</h2>
                {statusData.length > 0 ? (
                  <>
                    <div className="h-[140px] flex items-center justify-center">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={statusData} dataKey="value" cx="50%" cy="50%" innerRadius={40} outerRadius={60} paddingAngle={2}>
                            {statusData.map((entry, i) => (<Cell key={i} fill={entry.color} />))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {statusData.map((s, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 flex-shrink-0" style={{ backgroundColor: s.color }} />
                          <span className="text-[12px] text-[#6B7280]">{s.name}</span>
                          <span className="text-[12px] font-mono font-semibold text-[#0A0A0A] ml-auto">{s.value}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-[13px] text-[#9CA3AF] text-center py-8">No invoices yet</p>
                )}
              </div>

              <div className="bg-white border border-[#E5E7EB] p-4 sm:p-6" data-testid="top-clients">
                <div className="flex items-center gap-2 mb-4">
                  <UsersIcon className="w-4 h-4 text-[#1A3DFF]" />
                  <h2 className="font-semibold text-[16px] text-[#0A0A0A]">Top Clients</h2>
                </div>
                {data?.top_clients?.length > 0 ? (
                  <div className="space-y-3">
                    {data.top_clients.map((c, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="text-[12px] font-mono text-[#9CA3AF] w-5">{i + 1}.</span>
                          <span className="text-[13px] font-medium text-[#0A0A0A] truncate">{c.name}</span>
                        </div>
                        <span className="text-[13px] font-mono font-semibold text-[#0A0A0A] ml-2">{formatCurrency(c.revenue)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[13px] text-[#9CA3AF] text-center py-4">No paid invoices yet</p>
                )}
              </div>
            </div>
          </div>

          {/* Profit & Loss Overview */}
          {expData && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
              <div className="bg-white border border-[#E5E7EB] p-3 sm:p-5" data-testid="stat-total-income">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowDown className="w-4 h-4 text-[#059669]" />
                  <span className="text-[10px] sm:text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase">Total In</span>
                </div>
                <p className="font-display text-[20px] sm:text-[26px] tracking-[-0.02em] text-[#059669]">{formatCurrency(expData.total_income)}</p>
              </div>
              <div className="bg-white border border-[#E5E7EB] p-3 sm:p-5" data-testid="stat-total-expenses">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowUp className="w-4 h-4 text-[#EF4444]" />
                  <span className="text-[10px] sm:text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase">Total Out</span>
                </div>
                <p className="font-display text-[20px] sm:text-[26px] tracking-[-0.02em] text-[#EF4444]">{formatCurrency(expData.total_expenses)}</p>
              </div>
              <div className="bg-white border border-[#E5E7EB] p-3 sm:p-5" data-testid="stat-net-profit">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-4 h-4 text-[#1A3DFF]" />
                  <span className="text-[10px] sm:text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase">Net Profit</span>
                </div>
                <p className={`font-display text-[20px] sm:text-[26px] tracking-[-0.02em] ${expData.net_profit >= 0 ? "text-[#059669]" : "text-[#EF4444]"}`}>{formatCurrency(expData.net_profit)}</p>
              </div>
              <div className="bg-white border border-[#E5E7EB] p-3 sm:p-5" data-testid="stat-profit-margin">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-[#7C3AED]" />
                  <span className="text-[10px] sm:text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase">Margin</span>
                </div>
                <p className="font-display text-[20px] sm:text-[26px] tracking-[-0.02em] text-[#0A0A0A]">{expData.profit_margin}%</p>
              </div>
            </div>
          )}

          {/* Income vs Expenses Trend + Expense Breakdown */}
          {expData && (expData.monthly_trend?.length > 0 || expData.by_category?.length > 0) && (
            <div className="grid lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
              {expData.monthly_trend?.length > 0 && (
                <div className="lg:col-span-2 bg-white border border-[#E5E7EB] p-4 sm:p-6" data-testid="pnl-chart">
                  <h2 className="font-semibold text-[16px] text-[#0A0A0A] mb-1">Income vs Expenses</h2>
                  <p className="text-[13px] text-[#6B7280] mb-5">Monthly profit and loss trend</p>
                  <div className="h-[260px] sm:h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={expData.monthly_trend} barGap={2}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                        <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={{ stroke: "#E5E7EB" }} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v.toLocaleString()}`} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar name="Income" dataKey="income" fill="#10B981" radius={[2, 2, 0, 0]} />
                        <Bar name="Expenses" dataKey="expenses" fill="#EF4444" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex items-center gap-6 mt-3 pt-3 border-t border-[#E5E7EB]">
                    <div className="flex items-center gap-2"><div className="w-3 h-3 bg-[#10B981]" /><span className="text-[12px] text-[#6B7280]">Income</span></div>
                    <div className="flex items-center gap-2"><div className="w-3 h-3 bg-[#EF4444]" /><span className="text-[12px] text-[#6B7280]">Expenses</span></div>
                  </div>
                </div>
              )}
              {expData.by_category?.length > 0 && (
                <div className="bg-white border border-[#E5E7EB] p-4 sm:p-6" data-testid="expense-by-category">
                  <div className="flex items-center gap-2 mb-4">
                    <Receipt className="w-4 h-4 text-[#EF4444]" />
                    <h2 className="font-semibold text-[16px] text-[#0A0A0A]">Expenses by Category</h2>
                  </div>
                  <div className="space-y-3">
                    {expData.by_category.map((c, i) => {
                      const pct = expData.total_expenses > 0 ? Math.round(c.amount / expData.total_expenses * 100) : 0;
                      return (
                        <div key={i}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[13px] font-medium text-[#0A0A0A]">{c.category}</span>
                            <span className="text-[13px] font-mono font-semibold text-[#0A0A0A]">{formatCurrency(c.amount)}</span>
                          </div>
                          <div className="h-1.5 bg-[#F3F4F6] w-full">
                            <div className="h-full bg-[#EF4444]" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-[10px] text-[#9CA3AF] font-mono">{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Monthly Breakdown Table */}
          <div className="bg-white border border-[#E5E7EB] overflow-hidden" data-testid="monthly-breakdown">
            <div className="p-4 sm:p-6 border-b border-[#E5E7EB]">
              <h2 className="font-semibold text-[16px] text-[#0A0A0A]">Monthly Breakdown</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[500px]">
                <thead>
                  <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
                    <th className="text-left px-4 sm:px-5 py-3 text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase">Month</th>
                    <th className="text-right px-4 sm:px-5 py-3 text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase">Invoices</th>
                    <th className="text-right px-4 sm:px-5 py-3 text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase">Paid</th>
                    <th className="text-right px-4 sm:px-5 py-3 text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase">Outstanding</th>
                    <th className="text-right px-4 sm:px-5 py-3 text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {[...chartData].reverse().filter(m => m.invoices > 0).map((m, i) => (
                    <tr key={i} className="border-b border-[#E5E7EB] hover:bg-[#F9FAFB]">
                      <td className="px-4 sm:px-5 py-3 text-[14px] font-medium text-[#0A0A0A]">{m.name}</td>
                      <td className="px-4 sm:px-5 py-3 text-right font-mono text-[14px] text-[#0A0A0A]">{m.invoices}</td>
                      <td className="px-4 sm:px-5 py-3 text-right font-mono text-[14px] text-[#059669]">{m.Paid > 0 ? formatCurrency(m.Paid) : "\u2014"}</td>
                      <td className="px-4 sm:px-5 py-3 text-right font-mono text-[14px] text-[#D97706]">{m.Outstanding > 0 ? formatCurrency(m.Outstanding) : "\u2014"}</td>
                      <td className="px-4 sm:px-5 py-3 text-right font-mono text-[14px] font-semibold text-[#0A0A0A]">{formatCurrency(m.Paid + m.Outstanding)}</td>
                    </tr>
                  ))}
                  {chartData.every(m => m.invoices === 0) && (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-[14px] text-[#9CA3AF]">
                        No invoicing activity yet. Create your first invoice to see analytics.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </AppShell>
  );
};

export default Analytics;
